/**
 * Defer service-worker update reloads while something precious is on screen (an open Card
 * Table). sw-updater asks `requestReload(doReload)`; while any hold is active the reload is
 * remembered instead, subscribers show "Update ready — reload", and the reload runs when the user
 * taps it (`reloadNow`) or the last hold is released (they left the table), after a short grace.
 */
const holds = new Set<symbol>();
let pending: (() => void) | null = null;
const listeners = new Set<(pending: boolean) => void>();
const notify = () => {
  for (const l of [...listeners]) l(pending !== null);
};

/**
 * How long "nothing holds" must last before a deferred reload runs. The shell renders a different
 * tree below/above 768 px, so rotating a phone remounts the table page — and React runs the old
 * page's effect cleanup (release) before the new page's effect (hold). The grace absorbs that.
 */
export const RELOAD_RELEASE_GRACE_MS = 1_500;
let grace: ReturnType<typeof setTimeout> | null = null;
const cancelGrace = () => {
  if (grace) clearTimeout(grace);
  grace = null;
};

/** Hold reloads until the returned release is called. */
export function holdReloads(): () => void {
  const token = Symbol("reload-hold");
  holds.add(token);
  cancelGrace();
  return () => {
    if (!holds.delete(token) || holds.size > 0) return;
    cancelGrace();
    grace = setTimeout(() => {
      grace = null;
      if (holds.size > 0 || !pending) return;
      const run = pending;
      pending = null;
      notify();
      run();
    }, RELOAD_RELEASE_GRACE_MS);
  };
}

/** Reload now, or later if held (or within the grace after the last release). Returns true when it ran immediately. */
export function requestReload(doReload: () => void): boolean {
  if (holds.size === 0 && !grace) {
    doReload();
    return true;
  }
  pending = doReload;
  notify();
  return false;
}

export function isReloadPending(): boolean {
  return pending !== null;
}

export function onReloadPending(l: (pending: boolean) => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The user chose to reload now (e.g. between games). */
export function reloadNow(): void {
  const run = pending;
  pending = null;
  notify();
  run?.();
}

/** Test seam. */
export function resetReloadDefer(): void {
  cancelGrace();
  holds.clear();
  pending = null;
  listeners.clear();
}
